import { useState } from 'react';

/**
 * The notices — what DrBell is, the data it keeps, and what it needs from
 * a phone — as the websites show them (RX-TRUST-01, RX-REM-15).
 *
 * Two callers: the "Add a medicine" dialog asks for them at the first
 * medicine (AddMedicine's gate), and the caregiver site's set-up asks for
 * them first, before a record is made, exactly as the phone's first run
 * does (apps/mobile Onboarding → Consent). One copy of the cards, so the
 * two cannot say the notices differently.
 */
export interface Notice {
  id: string;
  version: number;
  title: string;
  requiredBeforeFirstUse: boolean;
  accepted: boolean;
  /** Paragraphs, as the API sends them. */
  body?: string[];
  /** The language the body is in. English when the requested one is not approved. */
  locale?: string;
}

/** The notices still owed: required before first use, and not yet agreed to. */
export const owedNotices = <T extends Notice>(documents: readonly T[]): T[] =>
  documents.filter((d) => !d.accepted && d.requiredBeforeFirstUse);

/** One card per notice: its title and every paragraph, as sent. */
export function NoticeList({ notices, tall }: { notices: readonly Notice[]; tall?: boolean }) {
  return (
    <div className="space-y-3">
      {notices.map((d) => (
        <div key={d.id} className="rounded-lg border border-(--line) bg-(--panel) p-3" lang={d.locale}>
          <p className="font-medium">{d.title}</p>
          {/* One paragraph each — rendered as one <pre>, the privacy notice
              ran together: "…no advertising.You can see everything…" */}
          {d.body && (
            <div className={`mt-2 space-y-2 overflow-auto text-sm leading-relaxed ${tall ? '' : 'max-h-48 text-xs'}`}>
              {d.body.map((para, i) => <p key={i}>{para}</p>)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The whole step, in a page: the notices in the reader's language, "The
 * English version governs" with the English one click away when they are
 * translated, and "I have read these", which records one agreement per
 * notice in the language actually on screen — as the phone does.
 */
export function FirstUseNotices({
  documents, loadEnglish, accept, onAccepted, onBack,
}: {
  documents: readonly Notice[];
  /** The same notices in English: the governing text. */
  loadEnglish: () => Promise<readonly Notice[]>;
  /** Record agreement to one notice, in the language it was shown in. */
  accept: (id: string, version: number, locale: string) => Promise<unknown>;
  onAccepted: () => void;
  onBack?: () => void;
}) {
  const [english, setEnglish] = useState<readonly Notice[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shown = english ?? documents;
  const owed = owedNotices(shown);
  const translated = owedNotices(documents).some((d) => d.locale && d.locale !== 'en-IN');

  return (
    <section aria-labelledby="notices-title">
      <h2 id="notices-title" className="text-xl font-semibold">Before you start</h2>
      <p className="mt-1 text-sm text-(--muted)">
        Setting up reminders is the first time health information is stored. These say what DrBell is
        and what happens to that information.
      </p>

      {translated && (
        <div className="mt-4 rounded-lg border border-(--line) p-3 text-sm">
          <p>This translation is for convenience. The English version governs.</p>
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              if (english) return setEnglish(null);
              setError(null);
              try {
                setEnglish(await loadEnglish());
              } catch {
                setError('Could not load the English version. Check your connection and try again.');
              }
            }}
            className="mt-1 min-h-11 font-medium text-(--accent) underline"
          >
            {english ? 'Back to my language' : 'Read the English version'}
          </button>
        </div>
      )}

      <div className="mt-4">
        <NoticeList notices={owed} tall />
      </div>

      {error && (
        <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">{error}</p>
      )}

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        {onBack && (
          <button type="button" onClick={onBack} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">
            Back
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              // One record per notice, in the language it was shown in:
              // agreement only to the text actually on this screen.
              for (const d of owed) await accept(d.id, d.version, d.locale ?? 'en-IN');
              onAccepted();
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
              setBusy(false);
            }
          }}
          className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
        >
          {busy ? 'Recording…' : 'I have read these'}
        </button>
      </div>
    </section>
  );
}
